import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft,
  FileText,
  Trash2,
  CheckCircle,
  Calendar,
  User,
  Tag,
  FolderOpen,
  Package,
  StickyNote,
  ExternalLink,
  Sparkles,
  Loader2,
  AlertTriangle,
  Link2,
  Zap,
} from 'lucide-react';
import ExtractedDataPanel from '@/components/document/ExtractedDataPanel';
import FieldReviewPanel from '@/components/document/FieldReviewPanel';
import PriceCheckPanel from '@/components/document/PriceCheckPanel';
import CreateOrderFromDocDialog from '@/components/document/CreateOrderFromDocDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOC_TYPES = ['Quote', 'Invoice', 'Order Confirmation', 'Packing Slip', 'Other'];

const DocumentDetailPage = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [projects, setProjects] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');

  // AI Parsing state
  const [parsing, setParsing] = useState(false);
  const [extraction, setExtraction] = useState(null);
  const [extractionId, setExtractionId] = useState(null);
  const [parsedFields, setParsedFields] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [corrections, setCorrections] = useState({});

  // Create order modal
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [orderProject, setOrderProject] = useState('');
  const [orderPhase, setOrderPhase] = useState('');
  const [phases, setPhases] = useState([]);
  const [creatingOrder, setCreatingOrder] = useState(false);

  // Smart Order Matches
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [linkingOrder, setLinkingOrder] = useState(null);


  const fetchOrdersForProject = useCallback(async (projectId) => {
    if (!projectId) { setOrders([]); return; }
    try {
      const res = await axios.get(`${API}/orders/project/${projectId}`, { withCredentials: true });
      setOrders(res.data);
    } catch { setOrders([]); }
  }, []);

  const fetchDocument = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/documents/${documentId}`, { withCredentials: true });
      setDoc(res.data);
      setNotes(res.data.notes || '');
      if (res.data.project_id) fetchOrdersForProject(res.data.project_id);
    } catch {
      toast.error('Document not found');
      navigate('/inbox');
    } finally {
      setLoading(false);
    }
  }, [documentId, navigate, fetchOrdersForProject]);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/`, { withCredentials: true });
      setProjects(res.data);
    } catch (err) {
      console.warn('Failed to load projects list:', err?.message || err);
    }
  }, []);

  const fetchPhasesForProject = async (projectId) => {
    if (!projectId) { setPhases([]); return; }
    try {
      const res = await axios.get(`${API}/phases/project/${projectId}`, { withCredentials: true });
      setPhases(res.data);
    } catch { setPhases([]); }
  };

  const handleUpdate = async (updates) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/documents/${documentId}`, updates, { withCredentials: true });
      setDoc(res.data);
      toast.success('Updated!');
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkReviewed = () => handleUpdate({ status: 'Reviewed' });
  const handleSaveNotes = () => handleUpdate({ notes });

  const handleDelete = async () => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/documents/${documentId}`, { withCredentials: true });
      toast.success('Document deleted');
      navigate('/inbox');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleProjectChange = async (projectId) => {
    await handleUpdate({ project_id: projectId || '' });
    if (projectId) fetchOrdersForProject(projectId);
    else setOrders([]);
    setTimeout(fetchDocument, 300);
  };

  const handleOrderChange = async (orderId) => {
    const updates = { order_id: orderId || '' };
    if (orderId) updates.status = 'Matched to Order';
    await handleUpdate(updates);
    setTimeout(fetchDocument, 300);
  };

  // ===== AI PARSING =====
  const applyParsedData = useCallback((data) => {
    setParsedFields({
      vendor_name: data.vendor_name || '',
      document_type: data.document_type || 'Other',
      po_number: data.po_number || '',
      quote_number: data.quote_number || '',
      invoice_number: data.invoice_number || '',
      order_date: data.order_date || '',
      expected_delivery_date: data.expected_delivery_date || '',
      payment_terms: data.payment_terms || '',
      tax: data.tax ?? 0,
      freight: data.freight ?? 0,
    });
    setLineItems((data.line_items || []).map((li, i) => ({
      ...li,
      _key: `li_${i}_${Date.now()}`,
      quantity: li.quantity ?? 0,
      unit: li.unit || 'each',
      unit_price: li.unit_price ?? 0,
      extended_price: li.extended_price ?? (li.quantity * li.unit_price) ?? 0,
    })));
  }, []);

  const fetchMatches = useCallback(async () => {
    setMatchesLoading(true);
    try {
      const res = await axios.get(`${API}/documents/${documentId}/matches`, { withCredentials: true });
      setMatches(res.data?.matches || []);
    } catch { /* ignore */ }
    finally { setMatchesLoading(false); }
  }, [documentId]);

  const checkExistingExtraction = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/documents/${documentId}/extraction`, { withCredentials: true });
      if (res.data && res.data.parsed_data) {
        setExtraction(res.data);
        setExtractionId(res.data.extraction_id);
        applyParsedData(res.data.parsed_data);
        fetchMatches();
      }
    } catch { /* no existing extraction */ }
  }, [documentId, applyParsedData, fetchMatches]);

  useEffect(() => {
    fetchDocument();
    fetchProjects();
  }, [fetchDocument, fetchProjects]);

  const docId = doc?.document_id;
  const parseStatus = doc?.parse_status;

  // Check for existing extraction on load
  useEffect(() => {
    if (docId) checkExistingExtraction();
  }, [docId, checkExistingExtraction]);

  // Poll when document is being auto-parsed
  useEffect(() => {
    if (parseStatus !== 'pending' && parseStatus !== 'parsing') return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/documents/${documentId}`, { withCredentials: true });
        setDoc(res.data);
        if (res.data.parse_status === 'parsed') {
          checkExistingExtraction();
          toast.success('Auto-parsing complete!');
        }
      } catch (err) {
        // Polling tick failed (network/transient) — keep polling
        console.warn('Auto-parse poll failed:', err?.message || err);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [parseStatus, documentId, checkExistingExtraction]);

  const handleParse = async () => {
    setParsing(true);
    try {
      const res = await axios.post(`${API}/documents/${documentId}/parse`, {}, { withCredentials: true });
      setExtractionId(res.data.extraction_id);
      setExtraction(res.data);
      applyParsedData(res.data.parsed_data);
      if (res.data.suggested_matches) {
        setMatches(res.data.suggested_matches);
      } else {
        fetchMatches();
      }
      toast.success('Document parsed successfully!');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Parsing failed';
      if (status === 503) toast.error('AI parsing is not yet configured. Contact your administrator.');
      else if (status === 422) toast.error('Could not parse AI response. Try again.');
      else toast.error(detail);
    } finally {
      setParsing(false);
    }
  };

  const handleReviewCorrection = useCallback((fieldPath, value) => {
    const m = fieldPath.match(/^line_items\.(\d+)\.(.+)$/);
    if (m) {
      const idx = parseInt(m[1], 10);
      const key = m[2];
      setLineItems((prev) => prev.map((li, i) => (i === idx ? { ...li, [key]: value } : li)));
    } else {
      setParsedFields((prev) => (prev ? { ...prev, [fieldPath]: value } : prev));
    }
  }, []);

  const sendCorrection = useCallback(async (fieldName, originalValue, correctedValue) => {
    if (!extractionId) return;
    try {
      await axios.post(`${API}/documents/${documentId}/corrections`, {
        extraction_id: extractionId,
        field_name: fieldName,
        original_value: originalValue,
        corrected_value: correctedValue,
      }, { withCredentials: true });
    } catch { /* non-blocking */ }
  }, [extractionId, documentId]);

  const handleFieldChange = (field, value) => {
    const original = corrections[field]?.original ?? parsedFields[field];
    if (!corrections[field]) {
      setCorrections(prev => ({ ...prev, [field]: { original } }));
    }
    setParsedFields(prev => ({ ...prev, [field]: value }));
    sendCorrection(field, original, value);
  };

  const handleLineItemChange = (idx, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      const original = updated[idx][field];
      updated[idx] = { ...updated[idx], [field]: value };
      if (field === 'quantity' || field === 'unit_price') {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : updated[idx].quantity;
        const price = field === 'unit_price' ? parseFloat(value) || 0 : updated[idx].unit_price;
        updated[idx].extended_price = Math.round(qty * price * 100) / 100;
      }
      sendCorrection(`line_items[${idx}].${field}`, original, value);
      return updated;
    });
  };

  const addLineItem = () => {
    setLineItems(prev => [...prev, {
      _key: `li_new_${Date.now()}`,
      product_description: '',
      quantity: 1,
      unit: 'each',
      unit_price: 0,
      extended_price: 0,
    }]);
  };

  const removeLineItem = (idx) => {
    setLineItems(prev => prev.filter((_, i) => i !== idx));
  };

  const subtotal = lineItems.reduce((sum, li) => sum + (li.extended_price || 0), 0);
  const totalAmount = subtotal + (parseFloat(parsedFields?.tax) || 0) + (parseFloat(parsedFields?.freight) || 0);

  // ===== CREATE ORDER =====
  const handleOpenCreateOrder = () => {
    setShowCreateOrder(true);
    setOrderProject(doc?.project_id || '');
    setOrderPhase('');
    if (doc?.project_id) fetchPhasesForProject(doc.project_id);
  };

  const handleLinkToMatch = async (match) => {
    setLinkingOrder(match.order_id);
    try {
      await axios.put(`${API}/documents/${documentId}`, {
        status: 'Matched to Order',
        order_id: match.order_id,
        project_id: match.project_id,
      }, { withCredentials: true });
      toast.success(`Linked to ${match.vendor_name} order (${match.po_number || match.status})`);
      fetchDocument();
      setMatches([]);
    } catch {
      toast.error('Failed to link order');
    } finally {
      setLinkingOrder(null);
    }
  };

  const handleOrderProjectChange = (pid) => {
    setOrderProject(pid);
    setOrderPhase('');
    fetchPhasesForProject(pid);
  };

  const handleCreateOrder = async () => {
    if (!orderProject) { toast.error('Select a project'); return; }
    setCreatingOrder(true);
    try {
      const payload = {
        project_id: orderProject,
        phase_id: orderPhase || null,
        vendor_name: parsedFields.vendor_name || 'Unknown Vendor',
        description: `Parsed from document: ${doc?.title || documentId}`,
        po_number: parsedFields.po_number || null,
        order_date: parsedFields.order_date || null,
        expected_delivery_date: parsedFields.expected_delivery_date || null,
        status: 'Draft',
        tax: parseFloat(parsedFields.tax) || 0,
        freight: parseFloat(parsedFields.freight) || 0,
        notes: `Auto-created from AI-parsed document ${doc?.title || ''}`,
        line_items: lineItems.map(li => ({
          product_description: li.product_description,
          quantity: parseFloat(li.quantity) || 0,
          unit: li.unit || 'each',
          unit_price: parseFloat(li.unit_price) || 0,
          product_id: li.product_id || null,
          manufacturer_sku: li.manufacturer_sku || null,
          match_confidence: li.match_confidence ?? null,
        })),
      };

      const res = await axios.post(`${API}/orders/`, payload, { withCredentials: true });
      const newOrderId = res.data.order_id;

      // Link document to the order
      await axios.put(`${API}/documents/${documentId}`, {
        status: 'Matched to Order',
        order_id: newOrderId,
        project_id: orderProject,
      }, { withCredentials: true });

      toast.success('Order created from parsed data!');
      setShowCreateOrder(false);
      navigate(`/projects/${orderProject}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create order');
    } finally {
      setCreatingOrder(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch { return 'N/A'; }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Quote': return 'bg-blue-500 text-white';
      case 'Invoice': return 'bg-purple-500 text-white';
      case 'Order Confirmation': return 'bg-green-500 text-white';
      case 'Packing Slip': return 'bg-orange-500 text-zinc-900';
      default: return 'bg-zinc-500 text-white';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'bg-primary text-zinc-900';
      case 'Reviewed': return 'bg-blue-500 text-white';
      case 'Matched to Order': return 'bg-green-500 text-white';
      default: return 'bg-zinc-100 text-zinc-900';
    }
  };

  const isEdited = (field) => corrections[field] !== undefined;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!doc) return null;

  const isImage = doc.file_type?.startsWith('image/');
  const isPdf = doc.file_type?.includes('pdf');
  const hasFile = !!doc.file_path;
  const hasText = !!doc.text_content;
  const hasContent = hasFile || hasText;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="document-detail-page">
      {/* Back Button */}
      <button
        onClick={() => navigate('/inbox')}
        data-testid="back-to-inbox-btn"
        className="inline-flex items-center gap-2 text-sm font-bold mb-4 hover:text-primary"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Inbox
      </button>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Main Content Area */}
        <div className="flex-1 min-w-0">
          {/* Document Header */}
          <div className="bg-white border border-zinc-200 p-4 sm:p-6 mb-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className={`px-2 py-0.5 text-xs font-bold uppercase border border-black ${getTypeColor(doc.document_type)}`}>
                    {doc.document_type}
                  </span>
                  <span className={`px-2 py-0.5 text-xs font-bold uppercase border border-black ${getStatusColor(doc.status)}`}>
                    {doc.status}
                  </span>
                </div>
                <h1 className="text-xl sm:text-2xl font-heading font-bold uppercase tracking-wide" data-testid="doc-detail-title">{doc.title}</h1>
              </div>
              <button
                onClick={handleDelete}
                data-testid="delete-doc-btn"
                className="h-10 px-3 bg-white text-destructive border border-zinc-200 hover:bg-destructive hover:text-white transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Content Display */}
          <div className="bg-white border border-zinc-200 mb-6 overflow-hidden" data-testid="doc-content-area">
            {hasFile && isImage && (
              <div className="p-4">
                <img
                  src={`${BACKEND_URL}${doc.file_path}`}
                  alt={doc.title}
                  className="max-w-full h-auto border border-zinc-200"
                  data-testid="doc-image-viewer"
                />
              </div>
            )}
            {hasFile && isPdf && (
              <div className="w-full" style={{ height: '70vh' }}>
                <iframe
                  src={`${BACKEND_URL}${doc.file_path}`}
                  title={doc.title}
                  className="w-full h-full border-0"
                  data-testid="doc-pdf-viewer"
                />
              </div>
            )}
            {hasFile && !isImage && !isPdf && (
              <div className="p-8 text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 text-zinc-500" />
                <p className="font-bold mb-2">{doc.file_name}</p>
                <a
                  href={`${BACKEND_URL}${doc.file_path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary font-bold underline"
                >
                  <ExternalLink className="w-4 h-4" /> Open File
                </a>
              </div>
            )}
            {hasText && (
              <div className="p-6">
                <pre className="whitespace-pre-wrap font-[Arial,sans-serif] text-sm leading-relaxed" data-testid="doc-text-content">
                  {doc.text_content}
                </pre>
              </div>
            )}
            {!hasFile && !hasText && (
              <div className="p-8 text-center text-zinc-500">
                <FileText className="w-16 h-16 mx-auto mb-4" />
                <p>No content attached</p>
              </div>
            )}
          </div>

          {/* AI Parse Button / Status */}
          {hasContent && !parsedFields && doc.parse_status !== 'parsing' && doc.parse_status !== 'pending' && (
            <button
              onClick={handleParse}
              disabled={parsing}
              data-testid="parse-document-btn"
              className="w-full h-14 bg-primary text-black font-heading font-bold uppercase tracking-wider text-sm border border-zinc-200 hover:bg-primary-hover transition-colors disabled:opacity-60 flex items-center justify-center gap-3 mb-6"
            >
              {parsing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Analyzing document...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Parse Document with AI
                </>
              )}
            </button>
          )}

          {hasContent && !parsedFields && (doc.parse_status === 'parsing' || doc.parse_status === 'pending') && (
            <div className="w-full bg-amber-50 border border-amber-200 p-4 mb-6 flex items-center gap-3" data-testid="auto-parsing-status">
              <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
              <div>
                <p className="font-heading font-bold text-sm uppercase tracking-wide text-amber-800">Auto-parsing in progress</p>
                <p className="text-xs text-amber-600">This document is being analyzed by AI. Results will appear automatically.</p>
              </div>
            </div>
          )}

          {!hasContent && !parsedFields && (
            <div className="bg-zinc-50 border border-zinc-200 p-6 text-center mb-6">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-zinc-400" />
              <p className="text-sm text-zinc-500">This document has no content to parse. Upload a file or paste text first.</p>
            </div>
          )}

          {/* ===== EXTRACTED DATA PANEL ===== */}
          {parsedFields && (
            <ExtractedDataPanel
              parsedFields={parsedFields}
              lineItems={lineItems}
              parsing={parsing}
              subtotal={subtotal}
              totalAmount={totalAmount}
              isEdited={isEdited}
              onReParse={handleParse}
              onFieldChange={handleFieldChange}
              onLineItemChange={handleLineItemChange}
              onAddLineItem={addLineItem}
              onRemoveLineItem={removeLineItem}
              onOpenCreateOrder={handleOpenCreateOrder}
            />
          )}

          {/* ===== FIELD REVIEW (corrections loop) ===== */}
          {parsedFields && doc?.extracted_data?.fields?.length > 0 && (
            <FieldReviewPanel
              documentId={documentId}
              extractedData={doc.extracted_data}
              onValueCorrected={handleReviewCorrection}
            />
          )}

          {/* ===== PRICE CHECK ===== */}
          {parsedFields && lineItems.length > 0 && (
            <PriceCheckPanel
              documentId={documentId}
              vendorName={parsedFields.vendor_name}
              lineItems={lineItems}
            />
          )}

          {/* Smart Order Matches */}
          {parsedFields && matches.length > 0 && !doc.order_id && (
            <div className="bg-white border border-zinc-200 mb-6" data-testid="smart-matches-panel">
              <div className="flex items-center gap-2 px-4 sm:px-6 py-3 border-b border-zinc-200 bg-amber-50">
                <Zap className="w-4 h-4 text-amber-600" />
                <h2 className="font-heading font-bold text-sm uppercase tracking-wider text-amber-800">
                  Matching Orders Found
                </h2>
                <span className="ml-auto text-xs font-mono text-amber-600">{matches.length} match{matches.length !== 1 ? 'es' : ''}</span>
              </div>
              <div className="divide-y divide-zinc-100">
                {matches.map((m, i) => (
                  <div
                    key={m.order_id}
                    data-testid={`match-${i}`}
                    className="flex items-center gap-4 px-4 sm:px-6 py-3 hover:bg-zinc-50 transition-colors"
                  >
                    <div className={`w-2 h-10 flex-shrink-0 ${
                      m.confidence === 'high' ? 'bg-green-500' :
                      m.confidence === 'medium' ? 'bg-amber-400' : 'bg-zinc-300'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm">{m.vendor_name}</span>
                        {m.po_number && (
                          <span className="text-xs font-mono bg-zinc-100 border border-zinc-200 px-1.5 py-0.5">
                            PO: {m.po_number}
                          </span>
                        )}
                        <span className={`text-[10px] font-heading font-semibold uppercase tracking-wider px-1.5 py-0.5 ${
                          m.confidence === 'high' ? 'bg-green-100 text-green-700' :
                          m.confidence === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-500'
                        }`}>
                          {m.confidence}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-zinc-500 mt-0.5">
                        <span>{m.project_name}</span>
                        <span className="font-mono">${(m.total || 0).toLocaleString()}</span>
                        <span className="uppercase font-bold">{m.status}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleLinkToMatch(m)}
                      disabled={linkingOrder === m.order_id}
                      data-testid={`link-match-${i}`}
                      className="h-10 px-4 bg-primary text-black font-heading font-bold uppercase tracking-wider text-[10px] border border-zinc-200 hover:bg-primary-hover disabled:opacity-50 flex items-center gap-1.5 flex-shrink-0 transition-colors"
                    >
                      {linkingOrder === m.order_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <>
                          <Link2 className="w-3.5 h-3.5" />
                          Link
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Metadata Sidebar */}
        <div className="lg:w-80 space-y-4 flex-shrink-0">
          {/* Quick Actions */}
          <div className="bg-white border border-zinc-200 p-4">
            <h3 className="text-sm font-heading font-bold uppercase tracking-wide mb-3">Actions</h3>
            <div className="space-y-2">
              {doc.status === 'New' && (
                <button
                  onClick={handleMarkReviewed}
                  disabled={saving}
                  data-testid="mark-reviewed-btn"
                  className="w-full h-12 bg-blue-500 text-white font-bold uppercase border border-zinc-200 flex items-center justify-center gap-2 hover:bg-blue-600 disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4" />
                  Mark as Reviewed
                </button>
              )}
              {doc.status === 'Reviewed' && !doc.order_id && (
                <p className="text-xs text-zinc-500 text-center py-2">
                  Link to an order below or parse with AI to create one
                </p>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-white border border-zinc-200 p-4" data-testid="doc-metadata">
            <h3 className="text-sm font-heading font-bold uppercase tracking-wide mb-3">Details</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                <div>
                  <p className="text-xs font-bold uppercase text-zinc-500">Sender</p>
                  <p className="text-sm font-medium">{doc.sender || 'Not specified'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                <div>
                  <p className="text-xs font-bold uppercase text-zinc-500">Date Received</p>
                  <p className="text-sm font-medium">{formatDate(doc.date_received)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Tag className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                <div>
                  <p className="text-xs font-bold uppercase text-zinc-500">Type</p>
                  <select
                    value={doc.document_type}
                    onChange={(e) => handleUpdate({ document_type: e.target.value })}
                    data-testid="doc-detail-type-select"
                    className="h-8 border border-black bg-white text-sm px-2 focus:outline-none"
                  >
                    {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              {doc.file_name && (
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-bold uppercase text-zinc-500">File</p>
                    <p className="text-sm font-medium truncate">{doc.file_name}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Project & Order Assignment */}
          <div className="bg-white border border-zinc-200 p-4" data-testid="doc-assignment">
            <h3 className="text-sm font-heading font-bold uppercase tracking-wide mb-3">Assignment</h3>
            <div className="space-y-3">
              <div>
                <label className="flex items-center gap-2 text-xs font-bold uppercase text-zinc-500 mb-1">
                  <FolderOpen className="w-3.5 h-3.5" /> Assign to Project
                </label>
                <select
                  value={doc.project_id || ''}
                  onChange={(e) => handleProjectChange(e.target.value)}
                  data-testid="doc-detail-project-select"
                  className="w-full h-10 border border-zinc-200 bg-white text-sm px-2 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">-- No Project --</option>
                  {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                </select>
                {doc.project_name && (
                  <button
                    onClick={() => navigate(`/projects/${doc.project_id}`)}
                    className="text-xs text-primary font-bold mt-1 hover:underline"
                  >
                    Go to {doc.project_name}
                  </button>
                )}
              </div>

              <div>
                <label className="flex items-center gap-2 text-xs font-bold uppercase text-zinc-500 mb-1">
                  <Package className="w-3.5 h-3.5" /> Link to Order
                </label>
                <select
                  value={doc.order_id || ''}
                  onChange={(e) => handleOrderChange(e.target.value)}
                  disabled={!doc.project_id}
                  data-testid="doc-detail-order-select"
                  className="w-full h-10 border border-zinc-200 bg-white text-sm px-2 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                >
                  <option value="">-- No Order --</option>
                  {orders.map(o => (
                    <option key={o.order_id} value={o.order_id}>
                      {o.vendor_name}{o.po_number ? ` - ${o.po_number}` : ''} ({o.status})
                    </option>
                  ))}
                </select>
                {!doc.project_id && (
                  <p className="text-xs text-zinc-500 mt-1">Assign a project first</p>
                )}
                {doc.order_label && (
                  <p className="text-xs font-bold mt-1">{doc.order_label}</p>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white border border-zinc-200 p-4" data-testid="doc-notes-section">
            <h3 className="flex items-center gap-2 text-sm font-heading font-bold uppercase tracking-wide mb-3">
              <StickyNote className="w-4 h-4" /> Notes
            </h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this document..."
              data-testid="doc-notes-input"
              className="w-full h-24 border border-zinc-200 bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
            <button
              onClick={handleSaveNotes}
              disabled={saving || notes === (doc.notes || '')}
              data-testid="save-notes-btn"
              className="mt-2 w-full h-10 bg-primary text-zinc-900 font-bold uppercase text-xs border border-zinc-200 hover:bg-primary-hover disabled:opacity-50"
            >
              Save Notes
            </button>
          </div>
        </div>
      </div>

      <CreateOrderFromDocDialog
        open={showCreateOrder}
        onOpenChange={setShowCreateOrder}
        projects={projects}
        phases={phases}
        orderProject={orderProject}
        orderPhase={orderPhase}
        parsedFields={parsedFields}
        lineItems={lineItems}
        totalAmount={totalAmount}
        creatingOrder={creatingOrder}
        onOrderProjectChange={handleOrderProjectChange}
        onOrderPhaseChange={setOrderPhase}
        onConfirm={handleCreateOrder}
      />
    </div>
  );
};

export default DocumentDetailPage;
